import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import billingService from '../../services/billingService';
import stripePaymentService from '../../services/stripePaymentService';
import SubscriptionOverviewCard from './components/SubscriptionOverviewCard';
import InvoiceHistoryTable from './components/InvoiceHistoryTable';
import PaymentStatisticsCard from './components/PaymentStatisticsCard';
import StripePaymentModal from './components/StripePaymentModal';
import StripeInvoicesTable from './components/StripeInvoicesTable';
import PaymentReconciliationPanel from './components/PaymentReconciliationPanel';
import OverdueInvoicesPanel from './components/OverdueInvoicesPanel';
import SalaryDistributionOrchestrationPanel from './components/SalaryDistributionOrchestrationPanel';
import RealtimeReconciliationDashboard from './components/RealtimeReconciliationDashboard';


const BillingSubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState('invoices');
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stripeInvoices, setStripeInvoices] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [daysUntilBilling, setDaysUntilBilling] = useState(0);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [organizationType, setOrganizationType] = useState('private');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [stripeCustomer, setStripeCustomer] = useState(null);
  const [processingInvoices, setProcessingInvoices] = useState(false);
  const [showBankPortalModal, setShowBankPortalModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');

  useEffect(() => {
    loadData();
  }, [organizationType]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const companyData = await billingService?.getActiveCompany();
      setCompany(companyData);

      if (companyData?.id) {
        const [subscriptionData, tiersData, invoicesData, statsData, stripeInvoicesData, stripeCustomerData] = await Promise.all([
          billingService?.getSubscriptionDetails(companyData?.id),
          billingService?.getSubscriptionTiers(organizationType),
          billingService?.getInvoiceHistory(companyData?.id),
          billingService?.getPaymentStatistics(companyData?.id),
          stripePaymentService?.getInvoices(companyData?.id),
          stripePaymentService?.getStripeCustomer(companyData?.id)
        ]);

        setSubscription(subscriptionData);
        setTiers(tiersData);
        setInvoices(invoicesData);
        setStatistics(statsData);
        setStripeInvoices(stripeInvoicesData);
        setStripeCustomer(stripeCustomerData);

        const days = billingService?.calculateDaysUntilBilling(subscriptionData?.nextBillingDate);
        setDaysUntilBilling(days || 0);

        // Create Stripe customer if doesn't exist
        if (!stripeCustomerData && organizationType === 'private') {
          try {
            const newCustomer = await stripePaymentService?.createStripeCustomer(companyData);
            setStripeCustomer(newCustomer?.customer);
          } catch (err) {
            console.error('Failed to create Stripe customer:', err);
          }
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPendingInvoices = async () => {
    try {
      setProcessingInvoices(true);
      const result = await billingService?.processPendingInvoices(company?.id);
      await loadData();
      alert(`Invoice processing complete: ${result?.successful || 0} successful, ${result?.failed || 0} failed`);
    } catch (err) {
      setError(err?.message || 'Failed to process invoices');
    } finally {
      setProcessingInvoices(false);
    }
  };

  const handlePayInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handlePayViaBankPortal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentMethod('bank');
    setShowBankPortalModal(true);
  };

  const handleBankPaymentInitiated = (paymentInfo) => {
    console.log('Bank payment initiated:', paymentInfo);
    alert(`Payment initiated via ${paymentInfo?.bank}. Please complete the payment in your bank portal and return here. Your invoice will be updated once payment is confirmed.`);
    setShowBankPortalModal(false);
    setSelectedInvoice(null);
  };

  const handlePaymentSuccess = async (result) => {
    setShowPaymentModal(false);
    setSelectedInvoice(null);
    
    // Reload data to reflect payment
    await loadData();
    
    alert('Payment successful! Your invoice has been paid.');
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setError(error?.message || 'Payment failed. Please try again.');
  };

  const handleDownloadInvoice = (invoice) => {
    console.log('Download invoice:', invoice?.id);
    alert(`Invoice download functionality will be implemented. Invoice ID: ${invoice?.id}`);
  };

  const handleExportData = () => {
    console.log('Export billing data');
    alert('Export functionality will be implemented');
  };

  const tabs = [
    { id: 'invoices', label: 'Invoices', icon: 'receipt' },
    { id: 'stripe-invoices', label: 'Stripe Invoices', icon: 'credit-card' },
    { id: 'reconciliation', label: 'Payment Reconciliation', icon: 'check-circle' },
    { id: 'overdue', label: 'Overdue Invoices', icon: 'alert-circle' },
    { id: 'salary-distribution', label: 'Salary Distribution', icon: 'building-2' },
    { id: 'realtime-reconciliation', label: 'Real-Time Reconciliation', icon: 'activity' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={setGlobalFilter} />
        <ComplianceAlertBanner />
        <DataRefreshIndicator />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader2" className="animate-spin h-8 w-8 mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading billing data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      
      <main className="container mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Billing & Subscription Management</h1>
              <p className="text-muted-foreground">Manage your subscription, view invoices, and track payment status</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportData}
            >
              <Icon name="Download" size={16} className="mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Email Invoicing Information Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Icon name="Mail" size={20} className="text-blue-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">Invoice Delivery & Payment</h3>
              <p className="text-sm text-blue-800">
                {organizationType === 'private' ?'Invoices are automatically generated with Pacific RBS Ltd branding when your billing tier changes. Pay online using credit/debit cards through Stripe.' :'For public sector organizations, invoices are issued via email for payment. Contact us for custom pricing and payment arrangements.'}
              </p>
            </div>
          </div>
        </div>

        {/* Payment Method Clarification Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Icon name="AlertCircle" size={20} className="text-amber-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900 mb-2">Payment Method Information</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-amber-900 mb-1 flex items-center">
                    <Icon name="CreditCard" size={14} className="mr-1" />
                    Stripe Payment (Automated)
                  </h4>
                  <ul className="text-sm text-amber-800 ml-5 list-disc space-y-1">
                    <li>Instant card payment processing via Stripe API</li>
                    <li>Automatic invoice generation and email delivery</li>
                    <li>Real-time payment confirmation and status updates</li>
                    <li>Stored payment methods for recurring billing</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-amber-900 mb-1 flex items-center">
                    <Icon name="Building2" size={14} className="mr-1" />
                    Direct Payment (Manual Bank Portal)
                  </h4>
                  <ul className="text-sm text-amber-800 ml-5 list-disc space-y-1">
                    <li>Requires manual login to your bank portal (BSP, Kinabank, or Credit Bank)</li>
                    <li>You must email our finance team to request an invoice</li>
                    <li>Manual payment processing through corporate banking portal</li>
                    <li>Payment confirmation via bank statement - no automated processing</li>
                    <li>Manual reconciliation required by our team</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Contact Information */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Icon name="Info" size={20} className="text-primary mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground mb-1">Subscription Inquiries</h3>
              <p className="text-sm text-muted-foreground mb-2">
                For more information about subscription tiers, custom pricing, or enterprise solutions, please contact our team:
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="mailto:admin@hrpacificrbs.com"
                  className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                >
                  <Icon name="Mail" size={14} className="mr-1" />
                  admin@hrpacificrbs.com
                </a>
                <span className="text-sm text-muted-foreground">|</span>
                <span className="text-sm font-medium text-foreground">Pacific RBS Ltd</span>
              </div>
            </div>
          </div>
        </div>

        {/* Automatic Invoice Processing Button */}
        {organizationType === 'private' && stripeCustomer && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <Icon name="Zap" size={20} className="text-green-600 mr-3 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-green-900 mb-1">Automatic Invoice Generation</h3>
                  <p className="text-sm text-green-800">
                    Invoices are automatically created when you add employees and your billing tier changes. All invoices include Pacific RBS Ltd branding.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleProcessPendingInvoices()}
                disabled={processingInvoices}
              >
                {processingInvoices ? (
                  <>
                    <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Icon name="RefreshCw" size={16} className="mr-2" />
                    Process Pending
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md mb-6">
            <p className="font-medium">Notice</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {subscription && (
          <div className="mb-6">
            <SubscriptionOverviewCard
              subscription={subscription}
              tiers={tiers}
              daysUntilBilling={daysUntilBilling}
            />
          </div>
        )}

        <div className="bg-card rounded-lg shadow-sm border border-border">
          <div className="border-b border-border">
            <div className="flex space-x-1 p-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab?.id
                      ? 'bg-primary text-white' :'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} className="mr-2" />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'invoices' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-foreground mb-1">Invoice History</h2>
                  <p className="text-sm text-muted-foreground">
                    {organizationType === 'private' ?'Manage and pay your subscription invoices' :'Complete transaction records with downloadable receipts'}
                  </p>
                </div>
                
                {organizationType === 'private' && stripeInvoices?.length > 0 ? (
                  <StripeInvoicesTable
                    invoices={stripeInvoices}
                    onPayInvoice={handlePayInvoice}
                    onViewInvoice={handleDownloadInvoice}
                    onPayViaBankPortal={handlePayViaBankPortal}
                  />
                ) : (
                  <InvoiceHistoryTable
                    invoices={invoices}
                    onDownload={handleDownloadInvoice}
                  />
                )}
              </div>
            )}

            {/* Overdue Invoices Tab */}
            {activeTab === 'overdue' && (
              <OverdueInvoicesPanel
                companyId={company?.id}
                company={company}
                invoices={invoices}
                stripeInvoices={stripeInvoices}
                onRefresh={loadData}
                onReminderSent={() => {
                  console.log('Reminder sent');
                  loadData();
                }}
              />
            )}

            {/* Reconciliation Tab */}
            {activeTab === 'reconciliation' && (
              <div className="space-y-6">
                <PaymentReconciliationPanel companyId={company?.id} />
              </div>
            )}

            {activeTab === 'payment' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-foreground mb-1">Payment Status Dashboard</h2>
                  <p className="text-sm text-muted-foreground">
                    Overview of payment history and financial metrics
                  </p>
                </div>
                <PaymentStatisticsCard statistics={statistics} />
                
                <div className="mt-6 bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start">
                    <Icon name="Info" size={20} className="text-primary mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">Next Billing Date</p>
                      <p className="text-sm text-muted-foreground">
                        Your next billing date is {new Date(subscription?.nextBillingDate)?.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })} ({daysUntilBilling} days remaining). Amount: K{subscription?.monthlyPriceKina}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-foreground mb-1">Subscription Tier Comparison</h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Compare features and pricing across different subscription tiers
                  </p>
                  
                  {/* Organization Type Selector */}
                  <div className="flex items-center space-x-4 bg-muted/50 rounded-lg p-3 inline-flex">
                    <span className="text-sm font-medium text-foreground">Organization Type:</span>
                    <button
                      onClick={() => setOrganizationType('private')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        organizationType === 'private' ?'bg-primary text-white' :'bg-background text-muted-foreground hover:bg-card'
                      }`}
                    >
                      <Icon name="Building2" size={14} className="inline mr-2" />
                      Private Companies
                    </button>
                    <button
                      onClick={() => setOrganizationType('public')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        organizationType === 'public' ?'bg-primary text-white' :'bg-background text-muted-foreground hover:bg-card'
                      }`}
                    >
                      <Icon name="Landmark" size={14} className="inline mr-2" />
                      Public Sector
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {tiers?.map((tier) => {
                    const isCurrentTier = tier?.tierName === subscription?.currentTier;
                    return (
                      <div
                        key={tier?.id}
                        className={`rounded-lg p-6 border-2 ${
                          isCurrentTier
                            ? 'border-primary bg-primary/5' :'border-border bg-card'
                        }`}
                      >
                        {isCurrentTier && (
                          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary text-white mb-3">
                            <Icon name="CheckCircle" size={12} className="mr-1" />
                            Current Plan
                          </div>
                        )}
                        
                        <h3 className="text-lg font-bold text-foreground mb-2">
                          {billingService?.formatTierName(tier?.tierName)}
                        </h3>
                        
                        <div className="mb-4">
                          {organizationType === 'public' ? (
                            <div>
                              <span className="text-xl font-bold text-foreground">Custom Pricing</span>
                              <p className="text-xs text-muted-foreground mt-1">Contact us for quote</p>
                            </div>
                          ) : (
                            <div>
                              <span className="text-3xl font-bold text-foreground">K{tier?.monthlyPriceKina}</span>
                              <span className="text-sm text-muted-foreground">/month</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2 mb-6">
                          <div className="flex items-center text-sm text-foreground">
                            <Icon name="Users" size={14} className="mr-2 text-primary" />
                            {tier?.minEmployees}-{tier?.maxEmployees || '∞'} employees
                          </div>
                          <div className="flex items-center text-sm text-foreground">
                            <Icon name="Shield" size={14} className="mr-2 text-primary" />
                            Full compliance features
                          </div>
                          <div className="flex items-center text-sm text-foreground">
                            <Icon name="FileText" size={14} className="mr-2 text-primary" />
                            Unlimited reports
                          </div>
                          {organizationType === 'public' && (
                            <div className="flex items-center text-sm text-foreground">
                              <Icon name="Award" size={14} className="mr-2 text-primary" />
                              Government compliance
                            </div>
                          )}
                        </div>
                        
                        {!isCurrentTier && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => alert('Tier upgrade/downgrade functionality will be implemented')}
                          >
                            {tier?.minEmployees > subscription?.activeEmployeeCount ? 'Upgrade' : 'Downgrade'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'salary-distribution' && (
              <SalaryDistributionOrchestrationPanel />
            )}

            {activeTab === 'realtime-reconciliation' && (
              <RealtimeReconciliationDashboard />
            )}
          </div>
        </div>
      </main>

      {/* Stripe Payment Modal */}
      {showPaymentModal && selectedInvoice && stripeCustomer && (
        <StripePaymentModal
          invoice={selectedInvoice}
          customerInfo={{
            companyId: company?.id,
            stripeCustomerId: stripeCustomer?.stripe_customer_id,
            email: stripeCustomer?.email || company?.contactEmail,
            name: stripeCustomer?.name || company?.companyName,
            phone: stripeCustomer?.phone || company?.contactPhone
          }}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedInvoice(null);
          }}
        />
      )}
    </div>
  );
};

export default BillingSubscriptionManagement;